// src/App.js

import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";

import createAppTheme from "./theme";
import { useAuth } from "./contexts/AuthContext";
import AuthProvider from "./contexts/AuthContext";

import AppSnackbarProvider from "./components/AppSnackbarProvider";
import PageLayout from "./components/PageLayout";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import LojaRoute from "./components/LojaRoute";
import AuthRedirect from "./components/AuthRedirect";

import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Profile from "./pages/Profile";
import RegisterPartner from "./pages/RegisterPartner";
import RegisterCliente from "./pages/RegisterCliente";

import AdminDashboard from "./pages/AdminDashboard";
import LojaDashboard from "./pages/LojaDashboard";
import PartnerEmpresaHome from "./pages/PartnerEmpresaHome";
import ClientDashboard from "./pages/ClientDashboard";

import PartnerEmpresaClientes from "./pages/PartnerEmpresaClientes";
import PartnerEmpresaComissoes from "./pages/PartnerEmpresaComissoes";
import ClientDetail from "./pages/ClientDetail";

import AdminPartnersCreate from "./pages/admin/AdminPartnersCreate";
import AdminPartnersList from "./pages/AdminPartnersList";
import RelatorioAnual from "./pages/RelatorioAnual";
import AdminPagamentosPendentes from "./pages/AdminPagamentosPendentes";
import AdminEmpresaInfo from "./pages/AdminEmpresaInfo";
import AdminEmpresaClientesList from "./pages/AdminEmpresaClientesList";
import AdminEmpresaClientesCreate from "./pages/AdminEmpresaClientesCreate";
import AdminEmpresaComprasList from "./pages/AdminEmpresaComprasList";
import AdminEmpresaComprasCreate from "./pages/AdminEmpresaComprasCreate";
import EditClient from "./pages/EditClient";
import EditPurchase from "./pages/EditPurchase";
import AdminEmpresaComprasPagar from "./pages/AdminEmpresaComprasPagar";
import AdminClienteDetail from "./pages/AdminClienteDetail";
import AdminNotificacoes from "./pages/AdminNotificacoes";
import AdminNotificacoesArquivadas from "./pages/AdminNotificacoesArquivadas";
import AdminComunicacoes from "./pages/AdminComunicacoes";

import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import TermosCondicoes from "./pages/TermosCondicoes";
import LivroReclamacoes from "./pages/LivroReclamacoes";

function getSystemTheme() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function getSavedThemeMode() {
  if (typeof window === "undefined") return "system";
  return localStorage.getItem("app-theme-mode") || "system";
}

function ThemedApp({ children }) {
  const { isDarkMode } = useAuth();
  const [mode, setMode] = React.useState(getSavedThemeMode());

  React.useEffect(() => {
    const updateThemeMode = () => setMode(getSavedThemeMode());

    const mediaQuery = window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;

    const handleSystemThemeChange = () => {
      if (getSavedThemeMode() === "system") setMode("system");
    };

    window.addEventListener("storage", updateThemeMode);
    window.addEventListener("app-theme-change", updateThemeMode);

    if (mediaQuery) {
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleSystemThemeChange);
      } else {
        mediaQuery.addListener(handleSystemThemeChange);
      }
    }

    return () => {
      window.removeEventListener("storage", updateThemeMode);
      window.removeEventListener("app-theme-change", updateThemeMode);

      if (mediaQuery) {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", handleSystemThemeChange);
        } else {
          mediaQuery.removeListener(handleSystemThemeChange);
        }
      }
    };
  }, []);

  const isLoginPage =
    window.location.pathname === "/login" ||
    window.location.pathname === "/";

const theme = React.useMemo(() => {

    if (isLoginPage) {
        return createAppTheme(false);
    }

    let darkModeEnabled;

    if (mode === "dark") darkModeEnabled = true;
    else if (mode === "light") darkModeEnabled = false;
    else darkModeEnabled = getSystemTheme();

    return createAppTheme(darkModeEnabled ?? isDarkMode);

}, [mode, isDarkMode, isLoginPage]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppSnackbarProvider>{children}</AppSnackbarProvider>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemedApp>
        <Router>
          <Routes>
            <Route path="/" element={<AuthRedirect />} />

            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="/register" element={<RegisterCliente />} />
            <Route path="/register/cliente" element={<RegisterCliente />} />
            <Route path="/register-parceiro" element={<RegisterPartner />} />

            <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/termos-condicoes" element={<TermosCondicoes />} />
            <Route path="/livro-reclamacoes" element={<LivroReclamacoes />} />

            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <PageLayout>
                    <Profile />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/cliente"
              element={
                <PrivateRoute>
                  <PageLayout>
                    <ClientDashboard />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <PageLayout contentPaper={false}>
                    <PartnerEmpresaHome />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard/empresa/clientes"
              element={
                <PrivateRoute>
                  <PageLayout>
                    <PartnerEmpresaClientes />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard/empresa/cliente/:clientId"
              element={
                <PrivateRoute>
                  <PageLayout>
                    <ClientDetail />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard/empresa/comissoes"
              element={
                <PrivateRoute>
                  <PageLayout>
                    <PartnerEmpresaComissoes />
                  </PageLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/loja"
              element={
                <LojaRoute>
                  <PageLayout>
                    <LojaDashboard />
                  </PageLayout>
                </LojaRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminDashboard />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/notificacoes"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminNotificacoes />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/notificacoes/arquivadas"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminNotificacoesArquivadas />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/parceiros"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminPartnersList />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/parceiros/new"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminPartnersCreate />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/cliente/:clienteId"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminClienteDetail />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/cliente/:clienteId/edit"
              element={
                <AdminRoute>
                  <PageLayout>
                    <EditClient />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/info"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaInfo />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/clientes"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaClientesList />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/clientes/new"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaClientesCreate />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/compras"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaComprasList />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/compras/new"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaComprasCreate />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/empresa/:empresaId/compras/:compraId/pagar"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminEmpresaComprasPagar />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/compra/:compraId/edit"
              element={
                <AdminRoute>
                  <PageLayout>
                    <EditPurchase />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/relatorio"
              element={
                <AdminRoute>
                  <PageLayout>
                    <RelatorioAnual />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/pagamentos"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminPagamentosPendentes />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route
              path="/admin/comunicacoes"
              element={
                <AdminRoute>
                  <PageLayout>
                    <AdminComunicacoes />
                  </PageLayout>
                </AdminRoute>
              }
            />

            <Route path="*" element={<AuthRedirect />} />
          </Routes>
        </Router>
      </ThemedApp>
    </AuthProvider>
  );
}